import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Wind, Sparkles, Droplets, Flame, Circle, ShieldCheck, Building2, Plane, Phone, Mail, Globe, Instagram, Facebook, Linkedin, Youtube } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SectionBubbles from '../components/SectionBubbles';
import SEO from '../components/SEO';
import { services } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  wind: Wind,
  sparkles: Sparkles,
  droplets: Droplets,
  airVent: Wind,
  armchair: Circle,
  flame: Flame,
  circle: Circle,
  shieldCheck: ShieldCheck
};

const Home = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    fleetSize: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: formData.companyName,
          contact_name: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          fleet_size: formData.fleetSize,
          message: formData.message
        })
      });
      if (!res.ok) throw new Error('Submission failed');
      toast.success("Thank you for your interest. We'll contact you within 24 hours.");
      setFormData({ companyName: '', contactName: '', email: '', phone: '', fleetSize: '', message: '' });
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="landing-page">
      <SEO 
        title={t('seo.home.title')}
        description={t('seo.home.description')}
        keywords={t('seo.home.keywords')}
        canonical="https://dsotocleaning.com"
      />
      
      <Navigation />

      {/* Hero Section with Video Background */}
      <section className="hero-section" id="hero">
        {/* YouTube Video Background */}
        <div className="hero-video-container">
          <iframe
            className="hero-video-iframe"
            src="https://www.youtube.com/embed/s9uJfxwZ9lU?autoplay=1&mute=1&controls=0&loop=1&playlist=s9uJfxwZ9lU&modestbranding=1&playsinline=1&rel=0&showinfo=0&iv_load_policy=3&disablekb=1&enablejsapi=1"
            title="D Soto Cleaning Fleet Operations"
            allow="autoplay; fullscreen; accelerometer; gyroscope; picture-in-picture"
            allowFullScreen
            playsInline
          />
          {/* Fallback for mobile/autoplay issues */}
          <div className="hero-video-fallback" style={{ display: 'none' }}></div>
        </div>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">{t('home.hero_title')}</h1>
          <h2 className="hero-subtitle">{t('home.hero_subtitle')}</h2>
          <p className="hero-description">{t('home.hero_desc')}</p>
          <Button onClick={scrollToContact} size="lg" className="cta-button">
            {t('home.hero_cta')} <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section section-padding" id="about" data-animate>
        <SectionBubbles />
        <div className="container">
          <div className="content-grid">
            <div className="content-text">
              <h2 className="section-title">{t('home.about_title')}</h2>
              <p className="section-description">{t('home.about_p2')}</p>
            </div>
            <div className="content-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_0b1e91d0-9c1d-440a-9b84-aeb555888e53/artifacts/3q93azq1_Presentacion%20Feed%20Formal.jpg" 
                alt="D Soto Cleaning Team" 
                className="image-rounded"
              />
              <div className="about-social-row" data-testid="about-social-row">
                <a href="https://www.instagram.com/dsotocleaningservice/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><Instagram size={20} /></a>
                <a href="https://www.facebook.com/profile.php?id=61588268131598" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={20} /></a>
                <a href="https://www.linkedin.com/company/d-soto-cleaning/posts/?feedView=all" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><Linkedin size={20} /></a>
                <a href="https://www.youtube.com/@DSotoCleaningServices" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><Youtube size={20} /></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section section-padding" id="services" data-animate>
        <SectionBubbles />
        <div className="container">
          <div className="section-logo-badge">
            <img
              src="https://customer-assets.emergentagent.com/job_3af646f4-3c0e-45dd-86e1-ef70869af78b/artifacts/epqs6wrz_Datos.png"
              alt="D Soto Cleaning"
              className="section-brand-logo"
            />
          </div>
          <h2 className="section-title text-center">{t('home.services_title')}</h2>
          <div className="services-grid">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Circle;
              return (
                <Card key={service.id} className="service-card">
                  <CardContent className="service-card-content">
                    <div className="service-icon">
                      <IconComponent size={32} />
                    </div>
                    <h3 className="service-title">{service.title}</h3>
                    <p className="service-description">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {/* Mobile compact list */}
          <div className="services-list-mobile">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Circle;
              return (
                <div key={service.id} className="service-list-item">
                  <div className="service-list-icon">
                    <IconComponent size={20} />
                  </div>
                  <div className="service-list-text">
                    <h3 className="service-list-title">{service.title}</h3>
                    <p className="service-list-desc">{service.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="value-section section-padding" id="value" data-animate>
        <SectionBubbles />
        <div className="container">
          <div className="content-grid reverse">
            <div className="content-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_3af646f4-3c0e-45dd-86e1-ef70869af78b/artifacts/p99mbmel_10.jpg" 
                alt="Professional Fleet Detailing - Aerial View" 
                className="image-rounded"
              />
            </div>
            <div className="content-text">
              <h2 className="section-title">More Than Cleaning. Operational Support.</h2>
              <p className="section-description">
                We don't just clean vehicles. We optimize fleet readiness. Our teams work behind the scenes 
                to ensure every vehicle meets corporate presentation standards before it reaches your customer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Scale Section */}
      <section className="scale-section section-padding" id="scale" data-animate>
        <div className="scale-background">
          <img 
            src="https://customer-assets.emergentagent.com/job_0b1e91d0-9c1d-440a-9b84-aeb555888e53/artifacts/770c8gbd_10-Feed-D_Soto-Febrero-2026.jpg" 
            alt="Large Scale Fleet Operations" 
            className="scale-bg-image"
          />
          <div className="scale-overlay"></div>
        </div>
        <div className="scale-content">
          <h2 className="section-title">Large Volume. No Compromise.</h2>
          <p className="section-description">
            We are not afraid of scale. From airport fleets to rental hubs, we manage high-volume operations 
            with efficiency and accountability.
          </p>
        </div>
      </section>

      {/* Fleet Operations at Scale Section */}
      <section className="fleet-ops-section section-padding" data-animate>
        <SectionBubbles />
        <div className="container">
          <h2 className="section-title text-center">{t('home.fleet_ops_title')}</h2>
          <p className="section-description text-center centered-text mb-12">{t('home.fleet_ops_desc')}</p>
          <div className="fleet-ops-grid">
            <div className="fleet-ops-image-wrapper">
              <img 
                src="https://customer-assets.emergentagent.com/job_3af646f4-3c0e-45dd-86e1-ef70869af78b/artifacts/uphlpiqi_11.jpg" 
                alt="Thousands of Fleet Vehicles Under Management" 
                className="fleet-ops-image"
              />
              <div className="fleet-ops-image-overlay">
                <span className="fleet-ops-badge">{t('home.fleet_ops_badge1')}</span>
              </div>
            </div>
            <div className="fleet-ops-image-wrapper">
              <img 
                src="https://customer-assets.emergentagent.com/job_3af646f4-3c0e-45dd-86e1-ef70869af78b/artifacts/qbnib32j_13.jpg" 
                alt="Fleet Operations Close-Up View" 
                className="fleet-ops-image"
              />
              <div className="fleet-ops-image-overlay">
                <span className="fleet-ops-badge">{t('home.fleet_ops_badge2')}</span>
              </div>
            </div>
          </div>
          <div className="fleet-ops-stats">
            <div className="fleet-ops-stat">
              <div className="fleet-ops-stat-number">{t('home.fleet_ops_stats.cities')}</div>
              <div className="fleet-ops-stat-label">{t('home.fleet_ops_stats.cities_label')}</div>
            </div>
            <div className="fleet-ops-stat">
              <div className="fleet-ops-stat-number">{t('home.fleet_ops_stats.availability')}</div>
              <div className="fleet-ops-stat-label">{t('home.fleet_ops_stats.availability_label')}</div>
            </div>
            <div className="fleet-ops-stat">
              <div className="fleet-ops-stat-number">{t('home.fleet_ops_stats.vehicles')}</div>
              <div className="fleet-ops-stat-label">{t('home.fleet_ops_stats.vehicles_label')}</div>
            </div>
            <div className="fleet-ops-stat">
              <div className="fleet-ops-stat-number">{t('home.fleet_ops_stats.years')}</div>
              <div className="fleet-ops-stat-label">{t('home.fleet_ops_stats.years_label')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Airport & Fleet Infrastructure Section */}
      <section className="airport-section" data-animate>
        <div className="airport-bg-wrapper">
          <img 
            src="https://customer-assets.emergentagent.com/job_3af646f4-3c0e-45dd-86e1-ef70869af78b/artifacts/6ogx6tx2_14.jpg" 
            alt="Airport Fleet Operations Infrastructure" 
            className="airport-bg-image"
          />
          <div className="airport-overlay"></div>
        </div>
        <div className="airport-content">
          <div className="container">
            <div className="airport-text-block">
              <div className="airport-label">
                <Plane size={20} />
                <span>{t('home.airport_label')}</span>
              </div>
              <h2 className="section-title text-white">{t('home.airport_title')}</h2>
              <p className="section-description text-white">{t('home.airport_desc')}</p>
              <div className="airport-features">
                <div className="airport-feature">
                  <Building2 size={20} />
                  <span>{t('home.airport_f1')}</span>
                </div>
                <div className="airport-feature">
                  <Plane size={20} />
                  <span>{t('home.airport_f2')}</span>
                </div>
                <div className="airport-feature">
                  <ShieldCheck size={20} />
                  <span>{t('home.airport_f3')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Fleet Operations Teaser */}
      <section className="home-mobile-section" data-animate data-testid="home-mobile-teaser">
        <div className="container">
          <div className="home-mobile-content">
            <div className="home-mobile-text">
              <div className="svc-eyebrow-light" style={{color: '#F7941D', borderColor: 'rgba(247,148,29,0.3)'}}>{t('home.mobile_eyebrow')}</div>
              <h2 className="home-mobile-title">
                {t('home.mobile_title_1')}
                <span className="home-mobile-title-accent">{t('home.mobile_title_2')}</span>
              </h2>
              <p className="home-mobile-desc">{t('home.mobile_desc')}</p>
              <div className="home-mobile-points">
                <span>{t('home.mobile_p1')}</span>
                <span>{t('home.mobile_p2')}</span>
                <span>{t('home.mobile_p3')}</span>
              </div>
              <div className="home-mobile-actions">
                <Link to="/services#mobile-service">
                  <Button size="lg" className="cta-button-orange" data-testid="home-mobile-cta">
                    {t('home.mobile_cta')} <ArrowRight className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="home-mobile-img">
              <img 
                src="https://customer-assets.emergentagent.com/job_8e095e6e-5bce-4e1e-981b-6fba51b00406/artifacts/0x1298ui_WhatsApp%20Image%202026-04-13%20at%2018.56.26%20%282%29.jpeg" 
                alt="D Soto Cleaning Mobile Fleet Service Van" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section section-padding" id="team" data-animate>
        <SectionBubbles />
        <div className="container">
          <div className="team-full-section">
            <h2 className="section-title text-center">{t('home.team_title')}</h2>
            <p className="section-description text-center centered-text mb-8">{t('home.team_desc')}</p>
            <div className="team-full-image-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_dsoto-corporate/artifacts/rgxbkgu8_2.jpg" 
                alt="D Soto Cleaning 400+ Professional Workforce" 
                className="team-full-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section section-padding" id="cta" data-animate>
        <div className="container text-center">
          <h2 className="section-title">Ready When Duty Calls</h2>
          <p className="section-description">{t('home.cta_desc')}</p>
          <Button onClick={scrollToContact} size="lg" className="cta-button">
            Contact D Soto Cleaning <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section section-padding" id="contact" data-animate>
        <SectionBubbles />
        <div className="container">
          <div className="contact-wrapper">
            <div className="contact-info">
              <h2 className="section-title">{t('home.contact_title')}</h2>
              <p className="section-description mb-8">{t('home.contact_desc')}</p>
              <div className="contact-details">
                <div className="contact-item">
                  <Phone className="contact-icon" />
                  <div>
                    <div className="contact-label">Phone</div>
                    <a href="tel:4079461710" className="contact-value">407-946-1710</a>
                  </div>
                </div>
                <div className="contact-item">
                  <Mail className="contact-icon" />
                  <div>
                    <div className="contact-label">Email</div>
                    <a href="mailto:Info@dsotocleaning.com" className="contact-value">Info@dsotocleaning.com</a>
                  </div>
                </div>
                <div className="contact-item">
                  <Globe className="contact-icon" />
                  <div>
                    <div className="contact-label">Website</div>
                    <a href="https://dsotocleaning.com" target="_blank" rel="noopener noreferrer" className="contact-value">
                      dsotocleaning.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="contact-form-wrapper">
              <form onSubmit={handleSubmit} className="contact-form">
                <Input
                  name="companyName"
                  placeholder="Company Name *"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <Input
                  name="contactName"
                  placeholder="Contact Name *"
                  value={formData.contactName}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <Input
                  name="email"
                  type="email"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <Input
                  name="phone"
                  type="tel"
                  placeholder="Phone Number *"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <Input
                  name="fleetSize"
                  placeholder="Fleet Size (approximate)"
                  value={formData.fleetSize}
                  onChange={handleChange}
                  className="form-input"
                />
                <Textarea
                  name="message"
                  placeholder="Tell us about your fleet detailing needs..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="form-input"
                />
                <Button type="submit" size="lg" className="submit-button" disabled={isSubmitting} data-testid="home-contact-submit-btn">
                  {isSubmitting ? 'Sending...' : <>Send Inquiry <ArrowRight className="ml-2" /></>}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
